import MapboxDraw from '@mapbox/mapbox-gl-draw'
import ListAltIcon from '@mui/icons-material/ListAlt'
import MapIcon from '@mui/icons-material/Map'
import { IconButton } from '@mui/material'
import { MainApi } from 'api/MainApi'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import OutlinedButton from 'components/Button/ButtonTypes/OutlinedButton'
import StandartButton from 'components/Button/ButtonTypes/StandartButton'
import Dropdown from 'components/Dropdown/Dropdown'
import { IOption } from 'components/Dropdown/Dropdown'
import Loader from 'components/Loader/Loader'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import UndoMapButton from 'components/MapboxMap/UndoMapButton/UndoMapButton'
import Title from 'components/Title/Title'
import { Feature } from 'geojson'
import { useIsMobile } from 'hooks/useIsMobile'
import { ReactComponent as DrawZoneSvg } from 'images/icons/cleaning_zone_small.svg'
import { ReactComponent as DeleteSvg } from 'images/icons/garbage_bin.svg'
import { ReactComponent as RemoveSvg } from 'images/icons/remove.svg'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSuppressNotificationSandwich } from 'hooks/useSuppressNotificationSandwich'
import { NOTIFICATION_SANDWICH_SUPPRESS } from 'utils/notificationSandwichGate'
import { IMapboxElement, IRobotMap } from 'utils/interfaces'

import RobotMapDrawControl from './RobotMapOnMap/RobotMapDrawControl'
import RobotMapOnMap from './RobotMapOnMap/RobotMapOnMap'
import RobotMapTable from './RobotMapTable/RobotMapTable'

import style from './RobotMapListView.module.css'
import MapTableToggleStyle from 'styles/MapTableToggle.module.css'
import mapPageFilterDropdown from 'styles/mapPageFilterDropdown.module.css'
import tablePagination from 'styles/tablePagination.module.css'

const EraseIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" {...props}>
    <path d="M15.14,3C14.63,3 14.12,3.2 13.73,3.59L2.59,14.73C1.81,15.5 1.81,16.77 2.59,17.56L5.03,20H12.69L21.41,11.27C22.2,10.5 22.2,9.23 21.41,8.44L16.56,3.59C16.17,3.2 15.65,3 15.14,3M17,18L15,20H22V18" />
  </svg>
)

const RobotMapListView = ({
  robotMaps,
  onMapsChanged,
  currentPage,
  totalCount,
  pageSize,
  onPageChanged,
  onPageSizeChange,
  searchQuery,
  onSearchChanged,
  facilityId,
  onFacilityChanged,
  selectedMapId,
  onSelectedMapIdChange,
}: {
  robotMaps: IRobotMap[]
  onMapsChanged: () => void
  currentPage: number
  totalCount: number
  pageSize: number
  onPageChanged: (page: number) => void
  onPageSizeChange?: (size: number) => void
  searchQuery: string
  onSearchChanged: (search: string) => void
  facilityId?: number
  onFacilityChanged: (id: number | undefined) => void
  selectedMapId?: number
  onSelectedMapIdChange?: (id: number | undefined) => void
}) => {
  const { t } = useTranslation()
  const mapRef = useRef<IMapboxElement>(null)
  const [selectedMap, setSelectedMap] = useState<IRobotMap | undefined>(undefined)
  const selectMap = (map: IRobotMap | undefined) => {
    setSelectedMap(map)
    onSelectedMapIdChange?.(map?.id)
  }
  const [facilities, setFacilities] = useState<any[]>([])
  const [pageInput, setPageInput] = useState(currentPage.toString())
  const isMobile = useIsMobile()
  const [showTable, setShowTable] = useState(true)
  const tableRef = useRef<HTMLDivElement>(null)
  const drawControlRef = useRef<MapboxDraw>(null)

  const [isEditing, setIsEditing] = useState(false)
  const [drawnFeatures, setDrawnFeatures] = useState<Feature[]>([])
  const [isDrawingMode, setIsDrawingMode] = useState(false)
  const [hasSelection, setHasSelection] = useState(false)
  const [appliedFeatures, setAppliedFeatures] = useState<Feature[]>([])
  const [history, setHistory] = useState<Feature[][]>([])
  const [redoHistory, setRedoHistory] = useState<Feature[][]>([])
  useSuppressNotificationSandwich(isEditing || isDrawingMode, NOTIFICATION_SANDWICH_SUPPRESS.ROBOT_MAP_EDIT)

  useEffect(() => {
    const tableElement = tableRef.current
    if (!tableElement) {
      return
    }

    const getFallbackRowHeight = () => {
      const styles = getComputedStyle(document.documentElement)
      const rowHeightVar = styles.getPropertyValue('--row-height').trim()
      const parsedRowHeight = Number.parseFloat(rowHeightVar)
      if (!Number.isNaN(parsedRowHeight) && parsedRowHeight > 0) {
        return parsedRowHeight
      }
      return 52
    }

    const recalculatePageSize = () => {
      const firstRow = tableElement.querySelector('[class*="tableRow"]') as HTMLElement | null
      const rowHeight = firstRow?.getBoundingClientRect().height || getFallbackRowHeight()
      if (rowHeight <= 0) {
        return
      }

      const maxVisibleRows = Math.max(1, Math.floor(tableElement.clientHeight / rowHeight))
      if (maxVisibleRows !== pageSize) {
        onPageSizeChange?.(maxVisibleRows)
      }
    }

    recalculatePageSize()

    const observer = new ResizeObserver(() => {
      recalculatePageSize()
    })
    observer.observe(tableElement)
    window.addEventListener('resize', recalculatePageSize)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', recalculatePageSize)
    }
  }, [isMobile, onPageSizeChange, pageSize, robotMaps.length, searchQuery, facilityId])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [facRes, mapsRes] = await Promise.all([
          MainApi.fetchFacilities(1, 1000),
          MainApi.fetchRobotMaps(1, 1000), // Get a large chunk to find all active facilities
        ])

        const activeFacilityIds = new Set(mapsRes.results.map(m => m.facility_id).filter((id): id is number => !!id))

        const filteredFacilities = facRes.results.filter(f => activeFacilityIds.has(f.id))
        setFacilities(filteredFacilities)
      } catch (e) {
        console.error('Error fetching filter data', e)
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    setPageInput(currentPage.toString())
  }, [currentPage])

  useEffect(() => {
    if (selectedMapId == null) {
      if (selectedMap) setSelectedMap(undefined)
      return
    }
    const found = robotMaps.find(m => m.id === selectedMapId)
    if (found && found.id !== selectedMap?.id) {
      setSelectedMap(found)
    }
  }, [selectedMapId, robotMaps])

  useEffect(() => {
    if (selectedMap) {
      const updatedMap = robotMaps.find(m => m.id === selectedMap.id)
      if (updatedMap) {
        setSelectedMap(updatedMap)
      }
    } else {
      setIsEditing(false)
      setDrawnFeatures([])
      setShowTable(true)
      setIsDrawingMode(false)
      setHasSelection(false)
      setAppliedFeatures([])
      setHistory([])
      setRedoHistory([])
    }
  }, [robotMaps, selectedMap?.id])

  const handlePageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(pageInput)
      const maxPage = Math.ceil(totalCount / pageSize) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        onPageChanged(val)
      } else {
        setPageInput(currentPage.toString())
      }
    }
  }

  const handleDeleteMap = async (map: IRobotMap) => {
    try {
      await MainApi.deleteRobotMap(map.id)
      if (selectedMap?.id === map.id) {
        selectMap(undefined)
      }
      onMapsChanged()
    } catch (e) {
      alert('Error deleting map')
    }
  }

  const maxPage = Math.ceil(totalCount / pageSize) || 1

  const facilitiesMap = facilities.reduce((acc, f) => {
    acc[f.id] = f.name
    return acc
  }, {} as Record<number, string>)

  const handleSaveErase = async () => {
    const allFeatures = [...appliedFeatures, ...drawnFeatures]
    if (!selectedMap || allFeatures.length === 0) {
      setIsEditing(false)
      setShowTable(true)
      setIsDrawingMode(false)
      setHasSelection(false)
      setAppliedFeatures([])
      setHistory([])
      setRedoHistory([])
      return
    }
    try {
      const polygons = allFeatures.map(f => f.geometry)
      await MainApi.eraseRobotMapObstacles(selectedMap.id, polygons)
      setIsEditing(false)
      setShowTable(true)
      setDrawnFeatures([])
      setIsDrawingMode(false)
      setHasSelection(false)
      setAppliedFeatures([])
      setHistory([])
      setRedoHistory([])
      onMapsChanged() // Reload the list so the map component refreshes tiles
    } catch (e) {
      alert('Error erasing obstacles')
    }
  }

  const handleApplyErase = () => {
    if (drawnFeatures.length === 0) return
    setHistory(prev => [...prev, appliedFeatures])
    setAppliedFeatures(prev => [...prev, ...drawnFeatures])
    setRedoHistory([])
    drawControlRef.current?.deleteAll()
    setDrawnFeatures([])
    setHasSelection(false)
    setIsDrawingMode(false)
  }

  const handleUndoErase = () => {
    if (history.length === 0) return
    const prev = history[history.length - 1]

    const undoneFeatures = appliedFeatures.filter(f => !prev.find(pf => pf.id === f.id))
    if (undoneFeatures.length > 0) {
      drawControlRef.current?.add({ type: 'FeatureCollection', features: undoneFeatures } as any)
      setDrawnFeatures(undoneFeatures)
      setIsDrawingMode(false)
      setHasSelection(true) // Automatically select the restored features

      // Auto-transition to direct_select mode if exactly one polygon is restored
      if (undoneFeatures.length === 1 && undoneFeatures[0].id) {
        const featureId = undoneFeatures[0].id as string
        setTimeout(() => {
          drawControlRef.current?.changeMode('direct_select', { featureId })
        }, 0)
      }
    }

    setHistory(h => h.slice(0, -1))
    setRedoHistory(r => [...r, appliedFeatures])
    setAppliedFeatures(prev)
  }

  const handleRedoErase = () => {
    if (redoHistory.length === 0) return
    const next = redoHistory[redoHistory.length - 1]

    const redoneFeatures = next.filter(f => !appliedFeatures.find(af => af.id === f.id))
    if (redoneFeatures.length > 0) {
      const redoneIds = redoneFeatures.map(f => f.id as string)
      drawControlRef.current?.delete(redoneIds)
      setDrawnFeatures([])
      setHasSelection(false)
      setIsDrawingMode(false)
    }

    setRedoHistory(r => r.slice(0, -1))
    setHistory(h => [...h, appliedFeatures])
    setAppliedFeatures(next)
  }

  const handleDrawCreate = (e: { features: Feature[] }) => {
    setDrawnFeatures(prev => [...prev, ...e.features])
    setIsDrawingMode(false)
    setHasSelection(true) // MapboxDraw usually selects the newly created feature
  }

  const handleDrawUpdate = (e: { features: Feature[] }) => {
    setDrawnFeatures(prev => {
      const newFeatures = [...prev]
      e.features.forEach(f => {
        const idx = newFeatures.findIndex(nf => nf.id === f.id)
        if (idx !== -1) newFeatures[idx] = f
      })
      return newFeatures
    })
  }

  const handleDrawDelete = (e: { features: Feature[] }) => {
    setDrawnFeatures(prev => prev.filter(f => !e.features.find(ef => ef.id === f.id)))
  }

  return (
    <div className={style.container}>
      <div className={style.main}>
        <div className={style.headerArea}>
          <div className={style.titleBlock}>
            <Title
              text={isEditing && selectedMap ? `Editing of ${selectedMap.name}` : 'Maps'}
              className={style.mobileTitle}
            />
            {isEditing && (
              <div className={style.editDrawControls}>
                <button
                  className={`${style.maintenanceButton} ${style.maintenanceButtonOrange}`}
                  title="Draw Area"
                  disabled={isDrawingMode}
                  onClick={() => {
                    drawControlRef.current?.changeMode('draw_polygon')
                    setIsDrawingMode(true)
                  }}
                >
                  <DrawZoneSvg width={24} height={24} />
                </button>
                <button
                  className={`${style.maintenanceButton} ${style.maintenanceButtonOrange}`}
                  title="Erase / Apply Mask"
                  disabled={drawnFeatures.length === 0}
                  onClick={handleApplyErase}
                >
                  <EraseIcon width={24} height={24} />
                </button>
                <button
                  className={`${style.maintenanceButton} ${style.maintenanceButtonGrey}`}
                  title="Delete Selected"
                  disabled={!hasSelection || isDrawingMode}
                  onClick={() => {
                    const draw = drawControlRef.current
                    if (!draw) return
                    const mode = draw.getMode()
                    if (mode === 'direct_select') {
                      const selectedPoints = draw.getSelectedPoints()
                      if (!selectedPoints || selectedPoints.features.length === 0) {
                        // Delete the whole polygon
                        const featureIds = draw.getSelectedIds()
                        if (featureIds && featureIds.length > 0) {
                          draw.delete(featureIds)
                          // Trigger update manually since delete doesn't fire trash event
                          setDrawnFeatures(prev => prev.filter(f => !featureIds.includes(f.id as string)))
                          setHasSelection(false)
                        }
                      } else {
                        draw.trash()
                      }
                    } else {
                      draw.trash()
                    }
                  }}
                >
                  <DeleteSvg width={24} height={24} />
                </button>
              </div>
            )}
          </div>
          {!isEditing && (
            <div className={style.controlsBlock}>
              <Dropdown
                className={mapPageFilterDropdown.filterDropdown}
                minWidth={isMobile ? 120 : 320}
                useSmallDropdownStyle={isMobile}
                currentOption={
                  facilityId
                    ? {
                        value: facilityId,
                        text: facilities.find(f => f.id === facilityId)?.name || 'N/A',
                      }
                    : undefined
                }
                placeholder="All Facilities"
                options={[
                  { value: '', text: 'All Facilities' },
                  ...facilities.map(f => ({
                    value: f.id,
                    text: f.name,
                  })),
                ]}
                onClickOption={(opt: IOption) => onFacilityChanged(opt.value ? Number(opt.value) : undefined)}
              />
              <input
                className={style.searchInput}
                type="text"
                placeholder="Search maps..."
                value={searchQuery}
                onChange={e => onSearchChanged(e.target.value)}
              />
            </div>
          )}
        </div>

        {isEditing && (
          <div className={style.bottomLeftActions}>
            <OutlinedButton
              onClick={() => {
                setIsEditing(false)
                setDrawnFeatures([])
                setShowTable(true)
                setIsDrawingMode(false)
                setHasSelection(false)
                setAppliedFeatures([])
                setHistory([])
                setRedoHistory([])
              }}
            >
              Cancel
            </OutlinedButton>
            <StandartButton onClick={handleSaveErase}>Save</StandartButton>
          </div>
        )}

        <Box className={clsx(style.box, { [style.hidden]: !showTable })}>
          <div className={style.tableWrapper}>
            <div className={style.tableScrollArea} ref={tableRef}>
              <RobotMapTable
                robotMaps={robotMaps}
                selectedMapId={selectedMap?.id}
                onMapSelect={selectMap}
                onEditMap={map => {
                  selectMap(map)
                  setIsEditing(true)
                  setShowTable(false) // Automatically switch to map view
                  setDrawnFeatures([])
                  setIsDrawingMode(false)
                  setHasSelection(false)
                  setAppliedFeatures([])
                  setHistory([])
                  setRedoHistory([])
                }}
                onDeleteMap={handleDeleteMap}
                onMapsChanged={onMapsChanged}
                facilitiesMap={facilitiesMap}
                currentPage={currentPage}
                pageSize={pageSize}
              />
            </div>
            {totalCount > pageSize && (
              <div className={tablePagination.pagination}>
                <button
                  className={tablePagination.paginationButton}
                  onClick={() => onPageChanged(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &larr;
                </button>
                <span className={tablePagination.pageInfo}>
                  <input
                    className={tablePagination.pageInput}
                    value={pageInput}
                    onChange={e => setPageInput(e.target.value)}
                    onKeyDown={handlePageJump}
                    onBlur={() => setPageInput(currentPage.toString())}
                  />
                  / {maxPage}
                </span>
                <button
                  className={tablePagination.paginationButton}
                  onClick={() => onPageChanged(currentPage + 1)}
                  disabled={currentPage >= maxPage}
                >
                  &rarr;
                </button>
              </div>
            )}
          </div>
        </Box>

        <div className={style.mapWrapper}>
          <MapboxCommon ref={mapRef} overlayItems={[]} onMapInited={() => {}} maxZoom={23}>
            {isEditing && (
              <UndoMapButton
                undoDisabled={history.length === 0}
                onUndo={handleUndoErase}
                redoDisabled={redoHistory.length === 0}
                onRedo={handleRedoErase}
              />
            )}
            <RobotMapOnMap
              selectedMap={selectedMap}
              panelOffset={showTable ? 580 : 0}
              erasedPolygons={appliedFeatures}
            />
            <RobotMapDrawControl
              ref={drawControlRef}
              isEditing={isEditing}
              onCreate={handleDrawCreate}
              onUpdate={handleDrawUpdate}
              onDelete={handleDrawDelete}
              onSelectionChange={e => {
                setHasSelection(e.features && e.features.length > 0)
                if (e.features && e.features.length === 1 && e.features[0].id) {
                  const featureId = e.features[0].id as string
                  // Ensure we are not already drawing or in direct_select
                  setTimeout(() => {
                    const currentMode = drawControlRef.current?.getMode()
                    if (currentMode === 'simple_select') {
                      drawControlRef.current?.changeMode('direct_select', { featureId })
                    }
                  }, 0)
                }
              }}
              onModeChange={e => setIsDrawingMode(e.mode === 'draw_polygon')}
            />
          </MapboxCommon>
        </div>
        {isMobile && (
          <button
            className={MapTableToggleStyle.toggle}
            onClick={() => setShowTable((prev: boolean) => !prev)}
            title={showTable ? 'Switch to Map' : 'Switch to Table'}
            style={{
              position: 'absolute',
              bottom: '24px',
              right: '24px',
            }}
          >
            {showTable ? (
              <MapIcon className={MapTableToggleStyle.icon} />
            ) : (
              <ListAltIcon className={MapTableToggleStyle.icon} />
            )}
          </button>
        )}
      </div>
    </div>
  )
}

export default RobotMapListView
